import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

// Interface for lock status
export interface LockStatus {
  sectionId: string;
  lockedBy: string | null;
}

// Enum for lock events (matching backend)
export enum LockEvents {
  Acquire = 'acquire',
  Release = 'release',
  Update = 'update',
}

// Enum for lock status (matching backend)
export enum LockStatusCode {
  Success = 'success',
  Failure = 'failure',
}

// Interface for lock event responses
export interface LockEventResponse {
  action: LockEvents;
  sectionId: string;
  status?: LockStatusCode;
  lockedBy?: string | null;
  reason?: string;
}

// Interface for section update event
export interface SectionUpdateEvent {
  sectionId: string;
  districtId: string;
  data_tables: string[];
  description: string;
  fields: string[];
  name: string;
  order: number;
  tabs: string[];
}

/**
 * Hook for managing active users, section locks, and section updates in a district via Socket.IO
 *
 * @param {string} serverUrl - The WebSocket server URL
 * @param {string} token - JWT authentication token
 * @param {boolean} autoConnect - Whether to connect automatically on mount
 * @returns {Object} Active users state, lock functions, connection control functions, and section updates
 */
const useUserCollaboration = (serverUrl: string, token: string, autoConnect = true) => {
  const [activeUsers, setActiveUsers] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sectionLocks, setSectionLocks] = useState<Map<string, string | null>>(new Map());
  const [sectionUpdates, setSectionUpdates] = useState<SectionUpdateEvent | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Connect to the socket
  const connect = useCallback(() => {
    if (socketRef.current?.connected) return;

    try {
      // Initialize socket with authentication header
      socketRef.current = io(serverUrl, {
        extraHeaders: {
          Authorization: `Bearer ${token}`,
        },
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      // Setup socket event listeners
      socketRef.current.on('connect', () => {
        setIsConnected(true);
        setError(null);
        console.log('Socket connected');
      });

      socketRef.current.on('disconnect', () => {
        setIsConnected(false);
        console.log('Socket disconnected');
      });

      socketRef.current.on('connect_error', (err) => {
        setError(`Connection error: ${err.message}`);
        console.error('Socket connection error:', err);
      });

      // Listen for active users updates
      socketRef.current.on('activeUsers', (users: string[]) => {
        setActiveUsers(users);
      });
      socketRef.current.on('factsheetSubmitted', (response: string) => {
        if (response) {
          window.location.reload();
        }
      });

      // Listen for lock events
      socketRef.current.on('lockEvent', (response: LockEventResponse) => {
        if (response.action === LockEvents.Update) {
          // Update lock status in state
          setSectionLocks((prev) => {
            const updated = new Map(prev);
            updated.set(response.sectionId, response.lockedBy || null);
            return updated;
          });
        }
      });

      // Listen for section updates
      socketRef.current.on('updateSection', (update: SectionUpdateEvent) => {
        setSectionUpdates(update);
      });
    } catch (err) {
      setError(
        `Failed to establish socket connection: ${err instanceof Error ? err.message : String(err)}`
      );
      console.error('Socket initialization error:', err);
    }
  }, [serverUrl, token]);

  // Disconnect from the socket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Request a lock for a section
  const requestLock = useCallback(
    (sectionId: string): Promise<LockEventResponse> => {
      return new Promise((resolve, reject) => {
        if (!socketRef.current || !isConnected) {
          reject(new Error('Socket not connected'));
          return;
        }

        // Set up one-time listener for the response
        socketRef.current.once('lockEvent', (response: LockEventResponse) => {
          if (response.action === LockEvents.Acquire && response.sectionId === sectionId) {
            if (response.status === LockStatusCode.Success) {
              // Update local state immediately for better UX
              setSectionLocks((prev) =>
                new Map(prev).set(response.sectionId, response.lockedBy || null)
              );
            }
            resolve(response);
          }
        });

        // Send lock request
        socketRef.current.emit('requestLock', { sectionId });
      });
    },
    [isConnected]
  );

  const requestToSubmit = useCallback((): Promise<any> => {
    return new Promise((reject) => {
      if (!socketRef.current || !isConnected) {
        reject(new Error('Socket not connected'));
        return;
      }

      // Send lock request
      socketRef.current.emit('factsheetSubmit');
    });
  }, [isConnected]);

  // Release a lock for a section
  const releaseLock = useCallback(
    (sectionId: string): Promise<LockEventResponse> => {
      return new Promise((resolve, reject) => {
        if (!socketRef.current || !isConnected) {
          reject(new Error('Socket not connected'));
          return;
        }

        // Set up one-time listener for the response
        socketRef.current.once('lockEvent', (response: LockEventResponse) => {
          if (response.action === LockEvents.Release && response.sectionId === sectionId) {
            if (response.status === LockStatusCode.Success) {
              // Update local state immediately for better UX
              setSectionLocks((prev) => {
                const newLocks = new Map(prev);
                newLocks.set(sectionId, null);
                return newLocks;
              });
            }
            resolve(response);
          }
        });

        // Send release request
        socketRef.current.emit('releaseLock', { sectionId });
      });
    },
    [isConnected]
  );

  const checkLockStatus = useCallback(
    (sectionId: string): Promise<LockEventResponse> => {
      return new Promise((resolve, reject) => {
        if (!socketRef.current || !isConnected) {
          reject(new Error('Socket not connected'));
          return;
        }

        socketRef.current.once('lockEvent', (response: LockEventResponse) => {
          if (response.sectionId === sectionId && response.action === LockEvents.Acquire) {
            if (response.status === LockStatusCode.Success) {
              setSectionLocks((prev) =>
                new Map(prev).set(response.sectionId, response.lockedBy || null)
              );
              // setSectionLocks(new Map([[response.sectionId, response.lockedBy || null]]));
            }
            resolve(response);
          }
        });

        socketRef.current.emit('lockStatus', { sectionId });
      });
    },
    [isConnected]
  );
  // Check if a section is locked and by whom
  const getSectionLockStatus = useCallback(
    (sectionId: string): string | null => {
      return sectionLocks.get(sectionId) || null;
    },
    [sectionLocks]
  );

  // Connect on mount if autoConnect is true
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  return {
    activeUsers,
    isConnected,
    error,
    connect,
    disconnect,
    requestLock,
    releaseLock,
    getSectionLockStatus,
    sectionLocks: Object.fromEntries(sectionLocks),
    sectionUpdates, // Add this to the returned object
    checkLockStatus,
    requestToSubmit,
  };
};

export default useUserCollaboration;
